<template>
  <div class="inst-post-snippet">
    <div class="inst-post-snippet__photo-wrap">
      <div v-if="currentImageIndex > 0" class="inst-post-snippet__scroll-arrow inst-post-snippet__scroll-arrow--left" @click="switchImagesList(-1)">
        <IconEmbedded name="caret-left_4" :size="18" color="rgba(0, 0, 0, 0.6)" />
      </div>
      <div v-if="currentImageIndex < props.imagesList.length - 1" class="inst-post-snippet__scroll-arrow inst-post-snippet__scroll-arrow--right" @click="switchImagesList(1)">
        <IconEmbedded name="caret-right_4" :size="18" color="rgba(0, 0, 0, 0.6)" />
      </div>
      <MediaThumbnail
        :class="{
          'inst-post-snippet__photo': true,
          'inst-post-snippet__photo--invisible': currentImageIndex !== index,
        }"
        v-for="(imageName, index) in props.imagesList"
        :media-url="imageName"
        :fancyboxKey="'InstPostPreview-' + props.fancyboxKey"
      />
    </div>

    <div class="inst-post-snippet__caption-textarea-wrap">
      <slot name="textarea" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";

// Components
import MediaThumbnail from "@components/MediaThumbnail.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

const props = withDefaults(
  defineProps<{
    imagesList: Array<string>;
    fancyboxKey: string;
  }>(),
  {
    imagesList: () => [],
    fancyboxKey: "",
  }
);

// Switch images list =========================================================
const currentImageIndex = ref<number>(0);

function switchImagesList(direction: number) {
  let newImageIndex = currentImageIndex.value;

  if (direction < 0) newImageIndex -= 1;
  else newImageIndex += 1;

  if (newImageIndex < 0) newImageIndex = 0;
  else if (newImageIndex > props.imagesList.length - 1) newImageIndex = props.imagesList.length - 1;

  currentImageIndex.value = newImageIndex;
}
</script>

<style scoped lang="scss">
@mixin placeholderElement {
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.1);
}

// Instagram post snippet =====================================================
.inst-post-snippet {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__user-info-skeleton {
    width: calc(100% - 32px);
    margin-bottom: 13px;
  }

  &__photo-wrap {
    width: 100%;
    aspect-ratio: 1/1;
    margin-bottom: 11px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    z-index: 0;
    overflow: hidden;
  }

  &__scroll-arrow {
    width: 40px;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: calc(50% - 20px);
    z-index: 1;
    cursor: pointer;
    user-select: none;

    &::before {
      content: "";
      width: 100%;
      height: 100%;
      border-radius: 100px;
      position: absolute;
      inset: 0 auto auto 0;
      z-index: -1;
      background: rgba(255, 255, 255, 0.8);
      transition:
        width 0.07s ease-in-out,
        height 0.07s ease-in-out,
        inset 0.07s ease-in-out,
        background 0.07s ease-in-out;
    }

    &:hover::before {
      width: calc(100% + 6px);
      height: calc(100% + 6px);
      inset: -3px auto auto -3px;
      background: rgba(255, 255, 255, 1);
    }

    &--left {
      left: 10px;
    }

    &--right {
      right: 10px;
    }
  }

  &__photo {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: relative;
    z-index: 0;

    &--invisible {
      display: none;
    }
  }

  &__icon-buttons-panel {
    width: calc(100% - 26px);
    margin-bottom: 17px;
  }

  &__likes-text {
    @include placeholderElement;
    width: 74px;
    height: 17px;
    margin: 0 0 13px 16px;
    align-self: flex-start;
  }

  &__caption-textarea-wrap {
    width: calc(100% - 32px);
    margin-bottom: 14px;

    :deep(textarea) {
      width: 100%;
      height: 95px;
      min-height: 95px;
      box-sizing: border-box;
      border: 1px #ababab solid;
      border-radius: 6px;
      color: #5b5b5b;
      font-family: sans-serif;
      transition: none !important;
    }
  }

  &__footer-skeleton {
    width: calc(100% - 32px);
  }
}
</style>
